// Broker Location create/edit modal — Settings-02 / Settings-02b.
//
// Field order matches US address conventions (Name → Status comment →
// Address → City / State / ZIP). Status is no longer a free-form
// select: the row's open/closed lifecycle is managed by the
// Deactivate/Restore actions on the LocationsTab. Country and time
// zone are gone — both columns dropped in Settings-02b.

import { useEffect, useMemo, useState } from "react";

import { Button, Modal } from "@/design-system";

import { useUsStatesQuery } from "./hooks";
import type {
  BrokerLocation,
  BrokerLocationCreateInput,
} from "./types";


export interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the modal hydrates from this row (edit mode);
   * when null, it operates in create mode. */
  location: BrokerLocation | null;
  saving: boolean;
  error: string | null;
  onSubmit: (input: BrokerLocationCreateInput) => void;
}


interface FormState {
  name: string;
  city: string;
  state_code: string;
  postal_code: string;
  address_line1: string;
  address_line2: string;
}


interface FieldErrors {
  name: string | null;
  state_code: string | null;
  postal_code: string | null;
}


const POSTAL_RE = /^\d{5}(-\d{4})?$/;


function blank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}


function initialState(location: BrokerLocation | null): FormState {
  if (!location) {
    return {
      name: "", city: "",
      state_code: "", postal_code: "",
      address_line1: "", address_line2: "",
    };
  }
  return {
    name: location.name,
    city: location.city || "",
    state_code: location.state_code || "",
    postal_code: location.postal_code || "",
    address_line1: location.address_line1 || "",
    address_line2: location.address_line2 || "",
  };
}


function emptyErrors(): FieldErrors {
  return { name: null, state_code: null, postal_code: null };
}


export function LocationModal({
  open, onClose, location, saving, error, onSubmit,
}: LocationModalProps) {
  const [form, setForm] = useState<FormState>(() => initialState(location));
  const [errors, setErrors] = useState<FieldErrors>(emptyErrors);
  const statesQuery = useUsStatesQuery();
  const states = useMemo(
    () => statesQuery.data?.states ?? [],
    [statesQuery.data],
  );

  useEffect(() => {
    if (open) {
      setForm(initialState(location));
      setErrors(emptyErrors());
    }
  }, [open, location]);

  const update = <K extends keyof FormState>(key: K, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as keyof FieldErrors]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const onPostalBlur = () => {
    // Strip whitespace + trailing dash before format check (matches
    // the backend's normalization). Empty is "not yet valid" rather
    // than an error — the submit-time validator catches it.
    const normalized = form.postal_code.trim().replace(/-+$/, "");
    if (!normalized) return;
    if (!POSTAL_RE.test(normalized)) {
      setErrors((prev) => ({
        ...prev,
        postal_code: "Enter a 5-digit ZIP or ZIP+4.",
      }));
    }
  };

  const validate = (): FieldErrors | null => {
    const next: FieldErrors = emptyErrors();
    if (!form.name.trim()) next.name = "Name is required.";
    if (!form.state_code) next.state_code = "State is required.";
    const zip = form.postal_code.trim().replace(/-+$/, "");
    if (!zip) {
      next.postal_code = "Postal code is required.";
    } else if (!POSTAL_RE.test(zip)) {
      next.postal_code = "Enter a 5-digit ZIP or ZIP+4.";
    }
    if (next.name || next.state_code || next.postal_code) return next;
    return null;
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setErrors(v);
      return;
    }
    onSubmit({
      name: form.name.trim(),
      address_line1: blank(form.address_line1),
      address_line2: blank(form.address_line2),
      city: blank(form.city),
      state_code: form.state_code,
      postal_code: form.postal_code.trim().replace(/-+$/, ""),
    });
  };

  const isEdit = location !== null;
  const submitDisabled =
    saving
    || !form.name.trim()
    || !form.state_code
    || !form.postal_code.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit location" : "Add location"}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="tertiary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="location-modal-submit"
            onClick={(e) =>
              onFormSubmit(e as unknown as React.FormEvent)
            }
            disabled={submitDisabled}
          >
            {saving ? "Saving…" : isEdit ? "Save" : "Add location"}
          </Button>
        </div>
      }
    >
      <form
        data-testid="location-modal-form"
        onSubmit={onFormSubmit}
        className="flex flex-col gap-3"
      >
        <Field label="Name" required error={errors.name}>
          <input
            data-testid="location-modal-name"
            type="text"
            value={form.name}
            placeholder="e.g., LA Headquarters, Northeast Distribution Center"
            onChange={(e) => update("name", e.target.value)}
            autoFocus
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Address line 1">
          <input
            data-testid="location-modal-address-line1"
            type="text"
            value={form.address_line1}
            onChange={(e) => update("address_line1", e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Suite, floor, unit" hint="optional">
          <input
            data-testid="location-modal-address-line2"
            type="text"
            value={form.address_line2}
            onChange={(e) => update("address_line2", e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>

        <div className="grid grid-cols-[2fr_1.5fr_1fr] gap-3">
          <Field label="City">
            <input
              data-testid="location-modal-city"
              type="text"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="State" required error={errors.state_code}>
            <select
              data-testid="location-modal-state"
              value={form.state_code}
              onChange={(e) => update("state_code", e.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">Select…</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Postal code" required error={errors.postal_code}>
            <input
              data-testid="location-modal-postal-code"
              type="text"
              inputMode="numeric"
              pattern="\d{5}(-\d{4})?"
              placeholder="90210"
              value={form.postal_code}
              onChange={(e) => update("postal_code", e.target.value)}
              onBlur={onPostalBlur}
              className={INPUT_CLASS}
            />
          </Field>
        </div>

        {error && (
          <p
            role="alert"
            data-testid="location-modal-error"
            className="text-xs font-medium text-ember-ink"
          >
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}


function Field({
  label, required = false, hint, error, children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      <span>
        {label}
        {required && <span className="text-ember-ink"> *</span>}
        {hint && (
          <span className="ml-1 text-text-tertiary">({hint})</span>
        )}
      </span>
      {children}
      {error && (
        <span
          role="alert"
          data-testid={`location-modal-error-${slugify(label)}`}
          className="text-[11px] font-medium text-ember-ink"
        >
          {error}
        </span>
      )}
    </label>
  );
}


function slugify(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}


const INPUT_CLASS =
  "w-full rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30";
